import { useMemo } from 'react';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import { Paper } from '@mui/material';
import { useTheme, alpha } from '@mui/material/styles';
import type {
  AtterbergGeneral,
  AtterbergDetail,
} from '../../../../types/atterberg';
import {
  applyOverrides,
  computeAtterbergResult,
} from '../computations';

interface Props {
  general: AtterbergGeneral;
  llTrials: AtterbergDetail[];
  plTrials: AtterbergDetail[];
}

// Reference lines per ASTM D2487
// A-line:  PI = 0.73 * (LL - 20). Horizontal at PI=4 extends from LL=12.44 (where U-line
//          crosses PI=4) up to LL=25.48 (where A-line itself reaches PI=4), then sloped
//          to LL=X_MAX.
// U-line:  PI = 0.9 * (LL - 8). Drawn dashed from LL=12.44 (PI=4) to LL=X_MAX. The lower
//          segment (PI=4 → PI=7) doubles as the LEFT boundary of the CL-ML zone.
// CL-ML:   parallelogram bounded by PI=4 (bottom), PI=7 (top), U-line (left, PI=4→7) and
//          A-line (right, PI=4→7). The PI=7 horizontal extends from LL=15.78 (U-line
//          crossing) to LL=29.59 (A-line crossing).
// Earlier versions (BUG-2026-0019) only drew the right portion of these segments —
// the leftward extensions and the U-line lower segment were missing.

const X_MAX = 75;
const Y_MAX = 45;
const A_LINE_AT_PI4 = 25.48;        // 0.73*(LL-20) = 4 → LL ≈ 25.48
const A_LINE_AT_PI7 = 7 / 0.73 + 20; // ≈ 29.59
const U_LINE_AT_PI4 = 4 / 0.9 + 8;   // ≈ 12.44
const U_LINE_AT_PI7 = 7 / 0.9 + 8;   // ≈ 15.78

export const PlasticityChart = ({ general, llTrials, plTrials }: Props) => {
  const theme = useTheme();

  const options = useMemo((): Highcharts.Options => {
    const result = computeAtterbergResult({
      llTrials, plTrials,
      method: general.llMethod ?? 'Multipoint',
      isNonPlastic: general.isNonPlastic ?? false,
    });
    const final = applyOverrides(general, result);

    // A-line sloped: (25.48, 4) → (X_MAX, 0.73*(X_MAX-20)). Inline label at the top end,
    // rotated to follow the line angle (≈36°), per BGC reference (no legend).
    const aLineEndPI = 0.73 * (X_MAX - 20);
    const aLine: Highcharts.SeriesLineOptions = {
      type: 'line',
      name: '"A" Line',
      data: [
        { x: A_LINE_AT_PI4, y: 4 } as Highcharts.PointOptionsObject,
        {
          x: X_MAX, y: aLineEndPI,
          dataLabels: {
            enabled: true,
            useHTML: false,
            format: '"A" Line',
            rotation: -36,
            x: -28, y: -8,
            style: { fontSize: '10px', fontStyle: 'italic', fontWeight: '600',
              color: theme.palette.text.primary, textOutline: '2px white' },
          },
        } as Highcharts.PointOptionsObject,
      ],
      color: theme.palette.text.primary,
      lineWidth: 1.5,
      marker: { enabled: false },
      enableMouseTracking: false,
      showInLegend: false,
    };
    // PI=4 horizontal — bottom of CL-ML zone. Extends from U-line crossing
    // (LL=12.44) to A-line crossing (LL=25.48).
    const aLineHorizontal: Highcharts.SeriesLineOptions = {
      type: 'line',
      name: '',
      data: [[U_LINE_AT_PI4, 4], [A_LINE_AT_PI4, 4]],
      color: theme.palette.text.primary,
      lineWidth: 1.5,
      marker: { enabled: false },
      enableMouseTracking: false,
      showInLegend: false,
    };

    // U-line dashed: from PI=4 (LL=12.44) all the way to LL=X_MAX. The PI=4→PI=7
    // segment is the LEFT boundary of the CL-ML zone. Inline label at the upper-right.
    const uLineEndPI = 0.9 * (X_MAX - 8);
    const uLine: Highcharts.SeriesLineOptions = {
      type: 'line',
      name: '"U" Line',
      data: [
        { x: U_LINE_AT_PI4, y: 4 } as Highcharts.PointOptionsObject,
        {
          x: X_MAX, y: uLineEndPI,
          dataLabels: {
            enabled: true,
            useHTML: false,
            format: '"U" Line',
            rotation: -42,
            x: -28, y: -8,
            style: { fontSize: '10px', fontStyle: 'italic', fontWeight: '600',
              color: theme.palette.text.primary, textOutline: '2px white' },
          },
        } as Highcharts.PointOptionsObject,
      ],
      color: theme.palette.text.primary,
      lineWidth: 1.5,
      dashStyle: 'Dash',
      marker: { enabled: false },
      enableMouseTracking: false,
      showInLegend: false,
    };

    // Vertical at LL=50
    const ll50: Highcharts.SeriesLineOptions = {
      type: 'line',
      name: '',
      data: [[50, 0], [50, Y_MAX]],
      color: theme.palette.text.primary,
      lineWidth: 1.5,
      marker: { enabled: false },
      enableMouseTracking: false,
      showInLegend: false,
    };

    // PI=7 horizontal — TOP of CL-ML zone. Spans from U-line crossing
    // (LL=15.78) to A-line crossing (LL=29.59).
    const clMlBoxTop: Highcharts.SeriesLineOptions = {
      type: 'line',
      name: '',
      data: [[U_LINE_AT_PI7, 7], [A_LINE_AT_PI7, 7]],
      color: theme.palette.text.primary,
      lineWidth: 1.5,
      marker: { enabled: false },
      enableMouseTracking: false,
      showInLegend: false,
    };

    // User point (LL, PI)
    const userSeries: Highcharts.SeriesScatterOptions[] = [];
    if (final.ll !== null && final.pi !== null && !general.isNonPlastic) {
      userSeries.push({
        type: 'scatter',
        name: 'Test Result',
        data: [{ x: final.ll, y: final.pi, name: final.uscs ?? '' }],
        color: theme.palette.error.main,
        marker: {
          radius: 6,
          symbol: 'circle',
          fillColor: 'transparent',
          lineColor: theme.palette.error.main,
          lineWidth: 2,
        },
        zIndex: 5,
        dataLabels: {
          enabled: true,
          formatter(this: Highcharts.Point) {
            const sym = (this as unknown as { name?: string }).name;
            return sym
              ? `${sym} (${this.x}, ${this.y})`
              : `(${this.x}, ${this.y})`;
          },
          style: { fontSize: '10px', fontWeight: '600', color: theme.palette.text.primary, textOutline: '2px white' },
          y: -8,
        },
      });
    }

    // Zone labels via plotLines/plotBands won't render text; use a dummy series
    // with dataLabels to place text. Each label becomes one point.
    const labelPoints: Highcharts.SeriesScatterOptions = {
      type: 'scatter',
      name: '',
      showInLegend: false,
      enableMouseTracking: false,
      marker: { enabled: false },
      dataLabels: {
        enabled: true,
        useHTML: false,
        // BUG-2026-0028 rework: center the TEXT BOX on the anchor. The default
        // placement draws the text ~14px ABOVE the anchor, so an anchor that
        // clears the sloped A/U-lines in data space still had its rendered box
        // clipped by them (measured −9.4px vs the U-line at a 560px panel).
        align: 'center',
        verticalAlign: 'middle',
        y: 0,
        style: { fontSize: '10px', fontWeight: '600', color: alpha(theme.palette.text.primary, 0.7), textOutline: 'none' },
        formatter(this: Highcharts.Point) {
          return (this as unknown as { label?: string }).label ?? '';
        },
      },
      // BUG-2026-0028 (rework): anchors are the CENTER of each label's text box
      // (see verticalAlign above). They were picked so the FULL box — roughly
      // ±3.9 LL wide and ±1.2 PI tall at the narrowest real panel (560px) —
      // clears every boundary line by ≥13px, not just the anchor point:
      //   A-line: PI = 0.73·(LL−20)   U-line: PI = 0.9·(LL−8)
      // — CL/OL (40,21): box top 22.2 vs U(36.1)=25.3; box bottom 19.8 vs A(43.9)=17.4
      // — CH/OH (60,37): box top 38.2 vs U(56.1)=43.3; bottom 35.8 vs A(63.9)=32.0
      // — ML/OL (40,5):  box top 6.2 vs A(36.1)=11.8
      // — MH/OH (64,22): box top 23.2 vs A(60.1)=29.3
      // — CL-ML (21,5.5): 9px font so the box fits the 3-PI-tall parallelogram
      //   (PI 4–7) with breathing room on both walls.
      data: [
        { x: 40, y: 21,  label: 'CL or OL' } as unknown as Highcharts.PointOptionsObject,
        { x: 60, y: 37,  label: 'CH or OH' } as unknown as Highcharts.PointOptionsObject,
        { x: 40, y: 5,   label: 'ML or OL' } as unknown as Highcharts.PointOptionsObject,
        { x: 64, y: 22,  label: 'MH or OH' } as unknown as Highcharts.PointOptionsObject,
        {
          // y=5.55 (not the 5.5 midpoint): the rendered text box sits slightly
          // below its anchor, so the nudge balances the top/bottom gaps.
          x: 21, y: 5.55, label: 'CL-ML',
          dataLabels: { style: { fontSize: '9px' } },
        } as unknown as Highcharts.PointOptionsObject,
      ],
    };

    return {
      chart: {
        height: 380,
        backgroundColor: 'transparent',
        spacing: [12, 16, 12, 12],
      },
      title: {
        text: 'Plasticity Chart (ASTM D2487)',
        style: { fontSize: '13px', fontWeight: '600' },
      },
      credits: { enabled: false },
      legend: { enabled: false },
      xAxis: {
        min: 0, max: X_MAX,
        title: { text: 'Liquid Limit (%)', style: { fontSize: '11px' } },
        tickInterval: 10, gridLineWidth: 1,
        labels: { style: { fontSize: '10px' } },
      },
      yAxis: {
        min: 0, max: Y_MAX,
        title: { text: 'Plasticity Index (%)', style: { fontSize: '11px' } },
        tickInterval: 10, gridLineWidth: 1,
        labels: { style: { fontSize: '10px' } },
      },
      tooltip: {
        formatter(this: Highcharts.Point) {
          if (this.series.name === 'Test Result') {
            const sym = (this as unknown as { name?: string }).name;
            return `<b>${sym || 'Test'}</b><br/>LL = ${this.x}<br/>PI = ${this.y}`;
          }
          return false;
        },
      },
      series: [
        aLineHorizontal, aLine, uLine, ll50, clMlBoxTop, labelPoints,
        ...userSeries,
      ],
    };
  }, [general, llTrials, plTrials, theme.palette]);

  return (
    <Paper elevation={0} sx={{
      p: 1.5, borderRadius: 2,
      border: `1px solid ${alpha(theme.border?.color ?? '#ccc', 0.35)}`,
      backgroundColor: alpha(theme.palette.background.paper, 0.8),
    }}>
      <HighchartsReact highcharts={Highcharts} options={options} />
    </Paper>
  );
};
